'use client';
import React, { useState, useEffect, useRef, Suspense } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { useSearchParams } from 'next/navigation';

// ============ DATA ============
const ALL_STATIONS = [
  'Alger Centre', 'El Agha', 'Hussein Dey', 'El Harrach', 'Bab Ezzouar',
  'Dar El Beïda', 'Réghaïa', 'Rouiba', 'Corso', 'Boudouaou', 'Thénia',
  'Zéralda', 'Birtouta', 'Blida', 'Boufarik', 'Larbaa',
  'Tafourah', 'Khelifa Boukhalfa', 'Place des Martyrs', 'Grande Poste',
  'Aïssat Idir', 'Hamma', 'Jardin d\'Essai', 'El Harrach Centre',
  'El Harrach Gare', 'Les Fusillés', 'Bachdjarah', 'Bachdjarah Virgule',
  'Ain Naadja', 'Haï El Badr', 'Mer et Soleil',
  'Les Bananiers', 'Dergana', 'Bordj El Kiffan', 'Ain Taya',
  'Oran', 'Constantine', 'Annaba', 'Sétif', 'Batna', 'Tlemcen',
  'Béjaïa', 'Sidi Bel Abbès', 'Biskra', 'Skikda', 'Jijel',
  'Guelma', 'Souk Ahras', 'Tizi Ouzou', 'Boumerdès', 'Tipaza',
  'Chlef', 'Mostaganem', 'Mascara', 'Relizane', 'Tiaret',
  'Saïda', 'Laghouat', 'Médéa', 'Bouira', 'Bordj Bou Arreridj',
  'Khenchela', 'Oum El Bouaghi', 'Tébessa', 'El Oued',
  'Oran Centre', 'Es Sénia', 'Gambetta', 'Plateau', 'Bir El Djir',
  'Constantine Gare', 'Sidi Mabrouk', 'Zouaghi', 'Ali Mendjeli',
  'Sétif Centre', 'Aïn Arnat', 'Sétif Est',
  'Annaba Gare', 'Berrahal', 'El Bouni',
];

interface SearchResult {
  id: string;
  type: 'Train' | 'Métro' | 'Tram';
  from: string;
  to: string;
  departure: string;
  arrival: string;
  duration: string;
  price: number;
  stops: number;
  stopsList: string[];
  available: boolean;
  operator: string;
  class?: string;
}

const generateResults = (from: string, to: string): SearchResult[] => {
  const fromNorm = from.trim().toLowerCase();
  const toNorm = to.trim().toLowerCase();

  // Metro routes
  const metroStations = [
    'tafourah', 'khelifa boukhalfa', 'place des martyrs', 'grande poste',
    'aissat idir', 'hamma', 'jardin d\'essai', 'el harrach centre',
    'el harrach gare', 'les fusillés', 'bachdjarah', 'bachdjarah virgule',
    'ain naadja', 'haï el badr', 'mer et soleil', 'el harrach', 'hussein dey',
  ];
  const tramAlgerStations = ['les bananiers', 'dergana', 'bordj el kiffan', 'ain taya', 'hussein dey', 'el harrach'];
  const tramOranStations = ['oran centre', 'gambetta', 'plateau', 'bir el djir', 'es sénia'];
  const tramConstStations = ['constantine gare', 'sidi mabrouk', 'zouaghi', 'ali mendjeli'];
  const tramSetifStations = ['sétif centre', 'aïn arnat', 'sétif est'];
  const tramAnnabaStations = ['annaba gare', 'berrahal', 'el bouni'];

  const isMetro = metroStations.includes(fromNorm) || metroStations.includes(toNorm);
  const isTramAlger = tramAlgerStations.includes(fromNorm) || tramAlgerStations.includes(toNorm);
  const isTramOran = tramOranStations.includes(fromNorm) || tramOranStations.includes(toNorm);
  const isTramConst = tramConstStations.includes(fromNorm) || tramConstStations.includes(toNorm);
  const isTramSetif = tramSetifStations.includes(fromNorm) || tramSetifStations.includes(toNorm);
  const isTramAnnaba = tramAnnabaStations.includes(fromNorm) || tramAnnabaStations.includes(toNorm);

  if (isMetro) {
    return [
      { id: 'm1', type: 'Métro', from, to, departure: '06:00', arrival: '06:25', duration: '25min', price: 50, stops: 8, stopsList: ['Grande Poste', 'Aïssat Idir', 'Hamma', 'El Harrach Centre'], available: true, operator: 'ETUSA Métro' },
      { id: 'm2', type: 'Métro', from, to, departure: '06:04', arrival: '06:29', duration: '25min', price: 50, stops: 8, stopsList: ['Grande Poste', 'Aïssat Idir', 'Hamma', 'El Harrach Centre'], available: true, operator: 'ETUSA Métro' },
      { id: 'm3', type: 'Métro', from, to, departure: '06:08', arrival: '06:33', duration: '25min', price: 50, stops: 8, stopsList: ['Grande Poste', 'Aïssat Idir', 'Hamma', 'El Harrach Centre'], available: true, operator: 'ETUSA Métro' },
      { id: 'm4', type: 'Métro', from, to, departure: '06:12', arrival: '06:37', duration: '25min', price: 50, stops: 8, stopsList: ['Grande Poste', 'Aïssat Idir', 'Hamma', 'El Harrach Centre'], available: true, operator: 'ETUSA Métro' },
      { id: 'm5', type: 'Métro', from, to, departure: '07:00', arrival: '07:25', duration: '25min', price: 50, stops: 8, stopsList: ['Grande Poste', 'Aïssat Idir', 'Hamma', 'El Harrach Centre'], available: true, operator: 'ETUSA Métro' },
    ];
  }

  if (isTramAlger) {
    return [
      { id: 't1', type: 'Tram', from, to, departure: '05:30', arrival: '06:05', duration: '35min', price: 50, stops: 12, stopsList: ['Dergana', 'Bordj El Kiffan', 'Ain Taya'], available: true, operator: 'SETRAM Alger' },
      { id: 't2', type: 'Tram', from, to, departure: '05:36', arrival: '06:11', duration: '35min', price: 50, stops: 12, stopsList: ['Dergana', 'Bordj El Kiffan', 'Ain Taya'], available: true, operator: 'SETRAM Alger' },
      { id: 't3', type: 'Tram', from, to, departure: '06:00', arrival: '06:35', duration: '35min', price: 50, stops: 12, stopsList: ['Dergana', 'Bordj El Kiffan', 'Ain Taya'], available: true, operator: 'SETRAM Alger' },
    ];
  }

  if (isTramOran) {
    return [
      { id: 'to1', type: 'Tram', from, to, departure: '06:00', arrival: '06:40', duration: '40min', price: 50, stops: 14, stopsList: ['Gambetta', 'Plateau', 'Bir El Djir'], available: true, operator: 'SETRAM Oran' },
      { id: 'to2', type: 'Tram', from, to, departure: '06:08', arrival: '06:48', duration: '40min', price: 50, stops: 14, stopsList: ['Gambetta', 'Plateau', 'Bir El Djir'], available: true, operator: 'SETRAM Oran' },
    ];
  }

  if (isTramConst) {
    return [
      { id: 'tc1', type: 'Tram', from, to, departure: '06:00', arrival: '06:28', duration: '28min', price: 50, stops: 10, stopsList: ['Sidi Mabrouk', 'Zouaghi'], available: true, operator: 'SETRAM Constantine' },
      { id: 'tc2', type: 'Tram', from, to, departure: '06:10', arrival: '06:38', duration: '28min', price: 50, stops: 10, stopsList: ['Sidi Mabrouk', 'Zouaghi'], available: true, operator: 'SETRAM Constantine' },
    ];
  }

  if (isTramSetif) {
    return [
      { id: 'ts1', type: 'Tram', from, to, departure: '06:00', arrival: '06:22', duration: '22min', price: 50, stops: 8, stopsList: ['Aïn Arnat'], available: true, operator: 'SETRAM Sétif' },
    ];
  }

  if (isTramAnnaba) {
    return [
      { id: 'ta1', type: 'Tram', from, to, departure: '06:00', arrival: '06:30', duration: '30min', price: 50, stops: 9, stopsList: ['Berrahal'], available: false, operator: 'SETRAM Annaba' },
    ];
  }

  // SNTF Train routes
  const trainSchedules: Record<string, { duration: string; price: number; stops: number; stopsList: string[]; times: string[] }> = {
    'alger centre-oran': { duration: '3h45', price: 750, stops: 5, stopsList: ['Blida', 'Chlef', 'Relizane', 'Mostaganem'], times: ['06:00', '08:30', '12:00', '15:30', '18:00'] },
    'oran-alger centre': { duration: '3h45', price: 750, stops: 5, stopsList: ['Mostaganem', 'Relizane', 'Chlef', 'Blida'], times: ['05:30', '09:00', '13:00', '16:00', '19:30'] },
    'alger centre-constantine': { duration: '4h20', price: 850, stops: 4, stopsList: ['Boumerdès', 'Thénia', 'Bordj Bou Arreridj', 'Sétif'], times: ['06:00', '07:30', '12:00', '17:00'] },
    'constantine-alger centre': { duration: '4h20', price: 850, stops: 4, stopsList: ['Sétif', 'Bordj Bou Arreridj', 'Thénia', 'Boumerdès'], times: ['05:00', '08:00', '11:30', '16:00'] },
    'alger centre-annaba': { duration: '5h30', price: 950, stops: 6, stopsList: ['Thénia', 'Béjaïa', 'Jijel', 'Skikda', 'Guelma'], times: ['06:00', '10:00', '15:00'] },
    'annaba-alger centre': { duration: '5h30', price: 950, stops: 6, stopsList: ['Guelma', 'Skikda', 'Jijel', 'Béjaïa', 'Thénia'], times: ['05:00', '09:00', '14:00'] },
    'alger centre-blida': { duration: '1h10', price: 250, stops: 3, stopsList: ['Birtouta', 'Zéralda', 'Boufarik'], times: ['06:00', '07:00', '08:00', '09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00', '17:00'] },
    'blida-alger centre': { duration: '1h10', price: 250, stops: 3, stopsList: ['Boufarik', 'Zéralda', 'Birtouta'], times: ['05:30', '06:30', '07:30', '08:30', '09:30', '10:30', '11:30', '12:30', '13:30', '14:30', '15:30', '16:30'] },
    'alger centre-béjaïa': { duration: '3h00', price: 600, stops: 3, stopsList: ['Boumerdès', 'Thénia', 'Boudouaou'], times: ['07:00', '11:00', '15:00'] },
    'alger centre-batna': { duration: '5h00', price: 900, stops: 4, stopsList: ['Bordj Bou Arreridj', 'Sétif', 'Oum El Bouaghi'], times: ['06:30', '13:00'] },
    'alger centre-sétif': { duration: '2h30', price: 500, stops: 3, stopsList: ['Boumerdès', 'Thénia', 'Bordj Bou Arreridj'], times: ['06:00', '09:00', '13:00', '17:00'] },
    'sétif-alger centre': { duration: '2h30', price: 500, stops: 3, stopsList: ['Bordj Bou Arreridj', 'Thénia', 'Boumerdès'], times: ['05:30', '08:30', '12:30', '16:30'] },
    'oran-tlemcen': { duration: '2h15', price: 350, stops: 3, stopsList: ['Sidi Bel Abbès', 'Mascara'], times: ['07:00', '11:00', '15:00', '18:00'] },
    'tlemcen-oran': { duration: '2h15', price: 350, stops: 3, stopsList: ['Mascara', 'Sidi Bel Abbès'], times: ['06:00', '10:00', '14:00', '17:00'] },
    'constantine-annaba': { duration: '1h30', price: 300, stops: 2, stopsList: ['Guelma', 'Souk Ahras'], times: ['06:00', '09:00', '12:00', '15:00', '18:00'] },
    'annaba-constantine': { duration: '1h30', price: 300, stops: 2, stopsList: ['Souk Ahras', 'Guelma'], times: ['05:30', '08:30', '11:30', '14:30', '17:30'] },
    'alger centre-el harrach': { duration: '0h20', price: 100, stops: 2, stopsList: ['Hussein Dey', 'Bab Ezzouar'], times: ['06:00', '07:00', '08:00', '09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00', '17:00', '18:00'] },
    'alger centre-réghaïa': { duration: '0h45', price: 150, stops: 4, stopsList: ['Hussein Dey', 'El Harrach', 'Rouiba', 'Corso'], times: ['06:00', '07:30', '09:00', '11:00', '13:00', '15:00', '17:00'] },
    'alger centre-boudouaou': { duration: '1h15', price: 200, stops: 5, stopsList: ['Hussein Dey', 'El Harrach', 'Réghaïa', 'Rouiba', 'Corso'], times: ['06:00', '09:00', '12:00', '15:00', '17:30'] },
    'alger centre-thénia': { duration: '1h30', price: 250, stops: 6, stopsList: ['Hussein Dey', 'El Harrach', 'Réghaïa', 'Rouiba', 'Corso', 'Boudouaou'], times: ['06:30', '10:00', '14:00', '17:00'] },
  };

  const key = `${fromNorm}-${toNorm}`;
  const schedule = trainSchedules[key];

  if (schedule) {
    return schedule.times.map((t, i) => {
      const [h, m] = t.split(':').map(Number);
      const totalMin = h * 60 + m;
      const durMatch = schedule.duration.match(/(\d+)h(\d+)?/);
      const durMin = durMatch ? parseInt(durMatch[1]) * 60 + parseInt(durMatch[2] || '0') : 60;
      const arrMin = totalMin + durMin;
      const arrH = Math.floor(arrMin / 60) % 24;
      const arrM = arrMin % 60;
      const arr = `${arrH.toString().padStart(2, '0')}:${arrM.toString().padStart(2, '0')}`;
      return {
        id: `train-${i}`,
        type: 'Train' as const,
        from,
        to,
        departure: t,
        arrival: arr,
        duration: schedule.duration,
        price: schedule.price,
        stops: schedule.stops,
        stopsList: schedule.stopsList,
        available: true,
        operator: 'SNTF',
        class: i === 0 ? 'Première classe' : 'Deuxième classe',
      };
    });
  }

  // Generic fallback
  if (from && to) {
    return [
      { id: 'g1', type: 'Train', from, to, departure: '08:00', arrival: '12:00', duration: '4h00', price: 700, stops: 3, stopsList: ['Arrêt 1', 'Arrêt 2', 'Arrêt 3'], available: true, operator: 'SNTF' },
      { id: 'g2', type: 'Train', from, to, departure: '14:00', arrival: '18:00', duration: '4h00', price: 700, stops: 3, stopsList: ['Arrêt 1', 'Arrêt 2', 'Arrêt 3'], available: true, operator: 'SNTF' },
    ];
  }

  return [];
};

// ============ SVG ICONS ============
const TrainIcon = ({ size = 16, className = '' }: { size?: number; className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
    <rect width="16" height="16" x="4" y="3" rx="2" />
    <path d="M4 11h16" />
    <path d="M12 3v8" />
    <path d="m8 19-2 3" />
    <path d="m18 22-2-3" />
    <path d="M8 15h.01" />
    <path d="M16 15h.01" />
  </svg>
);

const MetroIcon = ({ size = 16, className = '' }: { size?: number; className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
    <path d="M4 14a1 1 0 0 1-.78-1.63l9.9-10.2a.5.5 0 0 1 .86.46l-1.92 6.02A1 1 0 0 0 13 10h7a1 1 0 0 1 .78 1.63l-9.9 10.2a.5.5 0 0 1-.86-.46l1.92-6.02A1 1 0 0 0 11 14z" />
  </svg>
);

const SearchIcon = ({ size = 16, className = '' }: { size?: number; className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
    <path d="m21 21-4.34-4.34" />
    <circle cx="11" cy="11" r="8" />
  </svg>
);

const MapPinIcon = ({ size = 16, className = '' }: { size?: number; className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
    <path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);

const ArrowRightIcon = ({ size = 16, className = '' }: { size?: number; className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
    <path d="M5 12h14" />
    <path d="m12 5 7 7-7 7" />
  </svg>
);

const CalendarIcon = ({ size = 16, className = '' }: { size?: number; className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
    <path d="M8 2v4" />
    <path d="M16 2v4" />
    <rect width="18" height="18" x="3" y="4" rx="2" />
    <path d="M3 10h18" />
  </svg>
);

const ClockIcon = ({ size = 16, className = '' }: { size?: number; className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
    <circle cx="12" cy="12" r="10" />
    <path d="M12 6v6l4 2" />
  </svg>
);

const ChevronDownIcon = ({ size = 16, className = '' }: { size?: number; className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
    <path d="m6 9 6 6 6-6" />
  </svg>
);

const GlobeIcon = ({ size = 16, className = '' }: { size?: number; className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
    <circle cx="12" cy="12" r="10" />
    <path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20" />
    <path d="M2 12h20" />
  </svg>
);

const MoonIcon = ({ size = 16, className = '' }: { size?: number; className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
    <path d="M20.985 12.486a9 9 0 1 1-9.473-9.472c.405-.022.617.46.402.803a6 6 0 0 0 8.268 8.268c.344-.215.825-.004.803.401" />
  </svg>
);

const SunIcon = ({ size = 16, className = '' }: { size?: number; className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
    <circle cx="12" cy="12" r="4" />
    <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41" />
  </svg>
);

const MenuIcon = ({ size = 20, className = '' }: { size?: number; className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
    <path d="M4 5h16" />
    <path d="M4 12h16" />
    <path d="M4 19h16" />
  </svg>
);

const XIcon = ({ size = 20, className = '' }: { size?: number; className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
    <path d="M18 6 6 18" />
    <path d="m6 6 12 12" />
  </svg>
);

const MailIcon = ({ size = 14, className = '' }: { size?: number; className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
    <path d="m22 7-8.991 5.727a2 2 0 0 1-2.009 0L2 7" />
    <rect x="2" y="4" width="20" height="16" rx="2" />
  </svg>
);

const InstagramIcon = ({ size = 16, className = '' }: { size?: number; className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
    <rect width="20" height="20" x="2" y="2" rx="5" ry="5" />
    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
    <line x1="17.5" x2="17.51" y1="6.5" y2="6.5" />
  </svg>
);

// ============ AUTOCOMPLETE ============
interface AutocompleteProps {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  label: string;
  icon: React.ReactNode;
  dark?: boolean;
}

function Autocomplete({ value, onChange, placeholder, label, icon, dark = false }: AutocompleteProps) {
  const [open, setOpen] = useState(false);
  const [filtered, setFiltered] = useState<string[]>([]);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (value.length >= 1) {
      const q = value.toLowerCase();
      setFiltered(ALL_STATIONS.filter(s => s.toLowerCase().includes(q)).slice(0, 8));
      setOpen(true);
    } else {
      setFiltered([]);
      setOpen(false);
    }
  }, [value]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const inputClass = dark
    ? 'w-full px-3 py-2.5 rounded-xl text-sm font-medium bg-white/15 border border-white/25 text-white placeholder-white/50 outline-none focus:border-primary-400 focus:bg-white/20 transition-all' :'w-full px-3 py-2.5 rounded-xl text-sm font-medium bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white placeholder-gray-400 outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 transition-all';

  const labelClass = dark
    ? 'block text-xs font-semibold text-white/80 mb-1.5 flex items-center gap-1' :'block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1.5 flex items-center gap-1';

  return (
    <div className="relative" ref={ref}>
      <label className={labelClass}>{icon}{label}</label>
      <input
        type="text"
        placeholder={placeholder}
        value={value}
        onChange={e => onChange(e.target.value)}
        onFocus={() => value.length >= 1 && setOpen(true)}
        className={inputClass}
        autoComplete="off"
      />
      {open && filtered.length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-gray-900 border border-gray-700 rounded-xl shadow-2xl z-50 overflow-hidden max-h-48 overflow-y-auto">
          {filtered.map(s => (
            <button
              key={s}
              type="button"
              className="w-full text-left px-3 py-2 text-sm text-gray-200 hover:bg-primary-600/30 hover:text-white transition-colors flex items-center gap-2"
              onMouseDown={() => { onChange(s); setOpen(false); }}
            >
              <MapPinIcon size={12} className="text-primary-400 flex-shrink-0" />
              {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ============ NAVBAR ============
function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dark, setDark] = useState(true);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handler);
    return () => window.removeEventListener('scroll', handler);
  }, []);

  useEffect(() => {
    if (dark) document.documentElement.classList.add('dark');
    else document.documentElement.classList.remove('dark');
  }, [dark]);

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      scrolled ? 'bg-gray-900/95 backdrop-blur-md shadow-lg' : 'bg-gray-900'
    }`}>
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-10">
        <div className="flex items-center justify-between h-16">
          <Link href="/home-page" className="flex items-center gap-2.5 group">
            <Image src="https://transitdz.site/_next/image?url=%2Fassets%2Fimages%2Fapp_logo.png&w=96&q=85" alt="Logo" width={36} height={36} className="flex-shrink-0" unoptimized />
            <div className="flex flex-col leading-none">
              <span className="font-bold text-lg tracking-tight text-white">Transit DZ</span>
              <span className="text-[10px] font-medium tracking-wider text-green-300">نقل الجزائر</span>
            </div>
          </Link>
          <div className="hidden lg:flex items-center gap-1">
            <Link href="/home-page" className="px-4 py-2 rounded-lg text-sm font-semibold text-white/80 hover:text-white hover:bg-white/10 transition-all">Accueil</Link>
            <Link href="/search-results" className="px-4 py-2 rounded-lg text-sm font-semibold bg-white/20 text-white transition-all">Rechercher</Link>
            <Link href="/transport-schedule" className="px-4 py-2 rounded-lg text-sm font-semibold text-white/80 hover:text-white hover:bg-white/10 transition-all">Horaires</Link>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setDark(d => !d)} className="p-2 rounded-lg text-white/80 hover:text-white hover:bg-white/10 transition-all">
              {dark ? <SunIcon size={18} /> : <MoonIcon size={18} />}
            </button>
            <button onClick={() => setMobileOpen(o => !o)} className="lg:hidden p-2 rounded-lg text-white hover:bg-white/10">
              {mobileOpen ? <XIcon size={20} /> : <MenuIcon size={20} />}
            </button>
          </div>
        </div>
      </div>
      <div className={`lg:hidden transition-all duration-300 overflow-hidden ${mobileOpen ? 'max-h-64 opacity-100' : 'max-h-0 opacity-0'}`}>
        <div className="bg-gray-900 border-t border-gray-800 px-4 py-4 space-y-1">
          <Link href="/home-page" className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold text-gray-300 hover:bg-gray-800">Accueil</Link>
          <Link href="/search-results" className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold bg-primary-600/20 text-primary-400">Rechercher</Link>
          <Link href="/transport-schedule" className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold text-gray-300 hover:bg-gray-800">Horaires</Link>
        </div>
      </div>
    </nav>
  );
}

// ============ RESULT CARD ============
interface ResultCardProps {
  result: SearchResult;
}

function ResultCard({ result }: ResultCardProps) {
  const [expanded, setExpanded] = useState(false);

  const typeColor = result.type === 'Train' ? 'text-primary-500 bg-primary-50 dark:bg-primary-900/20'
    : result.type === 'Métro'? 'text-blue-500 bg-blue-50 dark:bg-blue-900/20' :'text-amber-500 bg-amber-50 dark:bg-amber-900/20';

  const typeIcon = result.type === 'Métro' ? <MetroIcon size={14} /> : <TrainIcon size={14} />;

  return (
    <div className={`card p-5 hover:shadow-card-hover ${!result.available ? 'opacity-60' : ''}`}>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        {/* Left: Type + Route */}
        <div className="flex items-start gap-3 flex-1">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${typeColor}`}>
            {typeIcon}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md ${typeColor}`}>{result.type}</span>
              <span className="text-[10px] text-gray-400">{result.operator}</span>
              {result.class && <span className="text-[10px] text-gray-400">· {result.class}</span>}
              {!result.available && <span className="text-[10px] font-bold text-red-500 bg-red-50 dark:bg-red-900/20 px-2 py-0.5 rounded-md">Suspendu</span>}
            </div>
            <div className="flex items-center gap-3">
              <div className="text-center">
                <div className="text-xl font-extrabold text-gray-900 dark:text-white">{result.departure}</div>
                <div className="text-xs text-gray-500 truncate max-w-[100px]">{result.from}</div>
              </div>
              <div className="flex-1 flex flex-col items-center">
                <div className="text-xs text-gray-400 mb-1">{result.duration}</div>
                <div className="w-full flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full bg-primary-500 flex-shrink-0" />
                  <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700 relative">
                    <div className="absolute inset-0 bg-primary-500/30" style={{ width: '100%' }} />
                  </div>
                  <div className="w-2 h-2 rounded-full bg-primary-500 flex-shrink-0" />
                </div>
                <div className="text-[10px] text-gray-400 mt-1">{result.stops} arrêt{result.stops > 1 ? 's' : ''}</div>
              </div>
              <div className="text-center">
                <div className="text-xl font-extrabold text-gray-900 dark:text-white">{result.arrival}</div>
                <div className="text-xs text-gray-500 truncate max-w-[100px]">{result.to}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Right: Price + CTA */}
        <div className="flex sm:flex-col items-center sm:items-end gap-3 sm:gap-2">
          <div className="text-right">
            <div className="text-2xl font-extrabold text-primary-600 dark:text-primary-400">{result.price} <span className="text-sm font-semibold">DZD</span></div>
            <div className="text-xs text-gray-400">par personne</div>
          </div>
          {result.available ? (
            <button className="btn-primary text-sm px-5 py-2.5 whitespace-nowrap">
              Réserver
            </button>
          ) : (
            <button disabled className="px-5 py-2.5 rounded-xl text-sm font-semibold bg-gray-100 dark:bg-gray-800 text-gray-400 cursor-not-allowed">
              Indisponible
            </button>
          )}
        </div>
      </div>

      {/* Expand stops */}
      {result.stopsList.length > 0 && (
        <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-800">
          <button
            onClick={() => setExpanded(e => !e)}
            className="flex items-center gap-2 text-xs font-semibold text-gray-500 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
          >
            <ChevronDownIcon size={14} className={`transition-transform ${expanded ? 'rotate-180' : ''}`} />
            {expanded ? 'Masquer' : 'Voir'} les arrêts intermédiaires ({result.stopsList.length})
          </button>
          {expanded && (
            <div className="mt-3 flex flex-wrap gap-2">
              {result.stopsList.map(s => (
                <span key={s} className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400">
                  <MapPinIcon size={10} className="text-primary-400" />{s}
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ============ SEARCH RESULTS INNER ============
function SearchResultsInner() {
  const searchParams = useSearchParams();
  const [from, setFrom] = useState(searchParams.get('from') || '');
  const [to, setTo] = useState(searchParams.get('to') || '');
  const [date, setDate] = useState(searchParams.get('date') || '');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [typeFilter, setTypeFilter] = useState<'all' | 'Train' | 'Métro' | 'Tram'>('all');

  useEffect(() => {
    const f = searchParams.get('from');
    const t = searchParams.get('to');
    if (f && t) {
      setFrom(f);
      setTo(t);
      handleSearchWithValues(f, t);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearchWithValues = (f: string, t: string) => {
    setLoading(true);
    setSearched(true);
    setTimeout(() => {
      setResults(generateResults(f, t));
      setLoading(false);
    }, 400);
  };

  const handleSearch = () => {
    if (!from || !to) return;
    handleSearchWithValues(from, to);
    const params = new URLSearchParams();
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    if (date) params.set('date', date);
    window.history.pushState({}, '', `/search-results?${params.toString()}`);
  };

  const filteredResults = typeFilter === 'all' ? results : results.filter(r => r.type === typeFilter);

  const popularRoutes = [
    { from: 'Alger Centre', to: 'Oran' },
    { from: 'Alger Centre', to: 'Constantine' },
    { from: 'Alger Centre', to: 'Annaba' },
    { from: 'Alger Centre', to: 'Blida' },
    { from: 'Oran', to: 'Tlemcen' },
    { from: 'Constantine', to: 'Annaba' },
    { from: 'Alger Centre', to: 'Réghaïa' },
    { from: 'Alger Centre', to: 'Boudouaou' },
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      <Navbar />

      {/* Search Header */}
      <div className="bg-gray-900 pt-16">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 py-8">
          <h1 className="text-2xl font-extrabold text-white mb-6">
            {searched && from && to ? (
              <span className="flex items-center gap-2 flex-wrap">
                <MapPinIcon size={20} className="text-primary-400" />
                {from}
                <ArrowRightIcon size={18} className="text-primary-400" />
                {to}
              </span>
            ) : 'Rechercher un trajet'}
          </h1>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <Autocomplete
              value={from}
              onChange={setFrom}
              placeholder="Ex: Alger Centre"
              label="Départ · انطلاق"
              icon={<MapPinIcon size={11} />}
              dark
            />
            <Autocomplete
              value={to}
              onChange={setTo}
              placeholder="Ex: Oran"
              label="Arrivée · وصول"
              icon={<MapPinIcon size={11} />}
              dark
            />
            <div>
              <label className="block text-xs font-semibold text-white/80 mb-1.5 flex items-center gap-1">
                <CalendarIcon size={11} />Date · تاريخ
              </label>
              <input
                type="date"
                value={date}
                onChange={e => setDate(e.target.value)}
                className="w-full px-3 py-2.5 rounded-xl text-sm font-medium bg-white/15 border border-white/25 text-white outline-none focus:border-primary-400 focus:bg-white/20 transition-all [color-scheme:dark]"
              />
            </div>
            <div className="flex items-end">
              <button
                onClick={handleSearch}
                className="w-full flex items-center justify-center gap-2 py-2.5 px-5 rounded-xl font-bold text-sm text-white transition-all duration-200 active:scale-95 hover:scale-105"
                style={{ background: 'linear-gradient(135deg, #00A550 0%, #00C96B 100%)', boxShadow: '0 4px 20px rgba(0,165,80,0.4)' }}
              >
                <SearchIcon size={16} />
                <span>Rechercher</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar */}
          <div className="lg:w-72 flex-shrink-0">
            {/* Popular Routes */}
            <div className="card p-5 mb-6">
              <h3 className="font-bold text-gray-900 dark:text-white text-sm mb-4 flex items-center gap-2">
                <TrainIcon size={14} className="text-primary-500" />
                Trajets populaires
              </h3>
              <div className="space-y-2">
                {popularRoutes.map(r => (
                  <button
                    key={`${r.from}-${r.to}`}
                    onClick={() => { setFrom(r.from); setTo(r.to); handleSearchWithValues(r.from, r.to); }}
                    className="w-full flex items-center gap-2 p-2.5 rounded-xl text-xs font-semibold text-gray-600 dark:text-gray-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 hover:text-primary-600 dark:hover:text-primary-400 transition-colors text-left"
                  >
                    <MapPinIcon size={10} className="text-primary-400 flex-shrink-0" />
                    <span className="truncate">{r.from}</span>
                    <ArrowRightIcon size={10} className="flex-shrink-0" />
                    <span className="truncate">{r.to}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Filter by type */}
            {searched && results.length > 0 && (
              <div className="card p-5">
                <h3 className="font-bold text-gray-900 dark:text-white text-sm mb-4">Filtrer par type</h3>
                <div className="space-y-2">
                  {(['all', 'Train', 'Métro', 'Tram'] as const).map(t => (
                    <button
                      key={t}
                      onClick={() => setTypeFilter(t)}
                      className={`w-full flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-semibold transition-all ${
                        typeFilter === t
                          ? 'bg-primary-500 text-white' :'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
                      }`}
                    >
                      {t === 'all' ? 'Tous les types' : t}
                      <span className="ml-auto text-[10px] opacity-70">
                        ({t === 'all' ? results.length : results.filter(r => r.type === t).length})
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Results */}
          <div className="flex-1">
            {loading && (
              <div className="flex items-center justify-center py-20">
                <div className="flex flex-col items-center gap-4">
                  <div className="w-12 h-12 rounded-full border-4 border-primary-500/30 border-t-primary-500 animate-spin" />
                  <p className="text-gray-500 dark:text-gray-400 text-sm">Recherche en cours...</p>
                </div>
              </div>
            )}

            {!loading && searched && filteredResults.length === 0 && (
              <div className="card p-12 text-center">
                <TrainIcon size={48} className="text-gray-300 dark:text-gray-700 mx-auto mb-4" />
                <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">Aucun trajet trouvé</h3>
                <p className="text-gray-500 dark:text-gray-400 text-sm mb-6">
                  Aucun trajet disponible entre <strong>{from}</strong> et <strong>{to}</strong>.
                  Essayez d&apos;autres stations ou vérifiez l&apos;orthographe.
                </p>
                <div className="flex flex-wrap gap-2 justify-center">
                  {popularRoutes.slice(0, 4).map(r => (
                    <button
                      key={`${r.from}-${r.to}`}
                      onClick={() => { setFrom(r.from); setTo(r.to); handleSearchWithValues(r.from, r.to); }}
                      className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400 hover:bg-primary-100 transition-colors"
                    >
                      {r.from} → {r.to}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {!loading && searched && filteredResults.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    <span className="font-bold text-gray-900 dark:text-white">{filteredResults.length}</span> trajet{filteredResults.length > 1 ? 's' : ''} trouvé{filteredResults.length > 1 ? 's' : ''}
                  </p>
                  <div className="flex items-center gap-2 text-xs text-gray-400">
                    <ClockIcon size={12} />
                    Triés par heure de départ
                  </div>
                </div>
                <div className="space-y-4">
                  {filteredResults.map(r => <ResultCard key={r.id} result={r} />)}
                </div>
              </div>
            )}

            {!loading && !searched && (
              <div className="card p-12 text-center">
                <SearchIcon size={48} className="text-gray-300 dark:text-gray-700 mx-auto mb-4" />
                <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">Recherchez votre trajet</h3>
                <p className="text-gray-500 dark:text-gray-400 text-sm">
                  Entrez votre gare de départ et d&apos;arrivée pour trouver les horaires disponibles.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 text-white mt-16">
        <div className="max-w-screen-2xl mx-auto px-6 lg:px-8 py-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Image src="https://transitdz.site/_next/image?url=%2Fassets%2Fimages%2Fapp_logo.png&w=96&q=85" alt="Logo" width={32} height={32} unoptimized />
              <span className="font-bold text-white">Transit DZ</span>
            </div>
            <div className="flex items-center gap-4 text-sm text-gray-400">
              <a href="mailto:transitdz0@gmail.com" className="hover:text-primary-400 transition-colors flex items-center gap-1">
                <MailIcon size={12} />transitdz0@gmail.com
              </a>
              <a href="https://www.instagram.com/transit.dz" target="_blank" rel="noopener noreferrer" className="hover:text-primary-400 transition-colors flex items-center gap-1">
                <InstagramIcon size={12} />@transit.dz
              </a>
            </div>
            <p className="text-sm text-gray-500">© 2026 Transit DZ 🇩🇿</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default function SearchResultsPage() {
  return (
    <Suspense fallback={
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="w-12 h-12 rounded-full border-4 border-primary-500/30 border-t-primary-500 animate-spin" />
      </div>
    }>
      <SearchResultsInner />
    </Suspense>
  );
}
